
import { toCanvas, toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Dimensions } from '../types';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isDemoMode } from './demoMode';

// Helper function to save PDF on both Electron/Web and Android
const savePDF = async (pdf: jsPDF, filename: string) => {
  // On Android, save to Documents folder using Capacitor Filesystem
  if (Capacitor.getPlatform() === 'android') {
    try {
      const pdfBlob = pdf.output('blob');
      
      // Use FileReader for safe base64 conversion of binary data
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove the data URI prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });
      
      alert(`PDF saved successfully to Documents folder as ${filename}`);
      console.log('PDF saved:', result.uri);
    } catch (error: any) {
      console.error('Failed to save PDF on Android:', error);
      alert(`Failed to save PDF: ${error.message || 'Unknown error'}`);
      // Fallback to browser download
      pdf.save(filename);
    }
  } else {
    // On Electron/Web, use standard browser download
    pdf.save(filename);
  }
};

// --- RESOURCE CLEANUP UTILITIES ---
// These functions help prevent memory leaks by properly disposing of resources

/**
 * Clean up a canvas element to free memory
 */
export const cleanupCanvas = (canvas: HTMLCanvasElement | null | undefined): void => {
  if (!canvas) return;
  try {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Clear the canvas by setting size to 0 (frees memory)
      canvas.width = 0;
      canvas.height = 0;
    }
  } catch (e) {
    console.warn('Error cleaning up canvas:', e);
  }
};

/**
 * Remove cloned DOM nodes from the document
 */
export const cleanupClonedNode = (container: HTMLElement | null | undefined): void => {
  if (!container) return;
  try {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  } catch (e) {
    console.warn('Error cleaning up cloned node:', e);
  }
};

/**
 * Clean up a FileReader to release blob references
 */
export const cleanupFileReader = (reader: FileReader | null | undefined): void => {
  if (!reader) return;
  try {
    if (reader.readyState === FileReader.LOADING) {
      reader.abort();
    }
  } catch (e) {
    console.warn('Error cleaning up FileReader:', e);
  }
};

/**
 * Revoke a blob URL to free memory
 */
export const revokeBlobURL = (url: string | null | undefined): void => {
  if (!url || !url.startsWith('blob:')) return;
  try {
    URL.revokeObjectURL(url);
  } catch (e) {
    console.warn('Error revoking blob URL:', e);
  }
};

// NOTE: We switched from DOM-based PDF generation to Raster (Image) based PDF generation.
// This ensures that custom fonts (like Charles Wright) are "baked in" to the image 
// so they appear correctly on the user's computer even if they don't have the font installed.

// Helper function to resolve image paths correctly for both http and file protocols
// The browser has already resolved img.src, so we use it directly, but handle edge cases
const resolveImagePath = (img: HTMLImageElement): string => {
  // Use the browser's resolved src directly - it's already correct
  let imageSrc = img.src;
  
  // If it's already a data URL or absolute URL, use as-is
  if (imageSrc.startsWith('data:') || imageSrc.startsWith('http://') || imageSrc.startsWith('https://') || imageSrc.startsWith('file://')) {
    return imageSrc;
  }
  
  // For relative paths in file:// context, construct the full path
  if (window.location.protocol === 'file:') {
    const basePath = window.location.href.substring(0, window.location.href.lastIndexOf('/') + 1);
    const cleanPath = imageSrc.replace(/^\.\//, '').replace(/^\//, '');
    return basePath + cleanPath;
  }
  
  // For HTTP context, construct relative to origin
  if (imageSrc.startsWith('/')) {
    return window.location.origin + imageSrc;
  } else {
    return window.location.origin + '/' + imageSrc;
  }
};

// Helper to convert a font file to base64
const fontToBase64 = async (fontPath: string): Promise<string | null> => {
  try {
    const response = await fetch(fontPath);
    if (!response.ok) {
      console.warn(`Failed to load font: ${fontPath}`);
      return null;
    }
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = () => {
        console.warn(`Failed to convert font to base64: ${fontPath}`);
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn(`Error loading font ${fontPath}:`, e);
    return null;
  }
};

// Font CSS cache to avoid re-fetching fonts on every print
let fontCSSCache: string | null = null;
let fontCacheTimestamp: number = 0;
const FONT_CACHE_DURATION = 5 * 60 * 1000; // Cache for 5 minutes

// Storage for custom uploaded fonts (base64 data)
const customFontRegistry: Map<string, string> = new Map();

/**
 * Register a custom font for use in PDF export
 * Call this when a user uploads a custom TTF font
 * @param fontName The font-family name
 * @param base64Data The base64-encoded font data (with or without data URI prefix)
 */
export const registerCustomFont = (fontName: string, base64Data: string): void => {
  customFontRegistry.set(fontName, base64Data);
  // Invalidate cache so the new font is included
  fontCSSCache = null;
  console.log(`[PDF Export] Registered custom font: ${fontName}`);
};

/**
 * Unregister a custom font
 */
export const unregisterCustomFont = (fontName: string): void => {
  customFontRegistry.delete(fontName);
  fontCSSCache = null;
};

/**
 * Get all registered custom fonts
 */
export const getRegisteredCustomFonts = (): string[] => {
  return Array.from(customFontRegistry.keys());
};

// Helper to pre-load all fonts as base64 and return the CSS
export const preloadFontsAsBase64 = async (forceReload: boolean = false): Promise<string> => {
  // Return cached version if available and not expired
  const now = Date.now();
  if (!forceReload && fontCSSCache && (now - fontCacheTimestamp) < FONT_CACHE_DURATION) {
    return fontCSSCache;
  }

  const fontMap: { [key: string]: string } = {
    'Charles Wright Radial': './fonts/CharlesWrightRadial.ttf',
    'Charles Wright': './fonts/CharlesWright.ttf',
    'Euro': './fonts/Euro.ttf',
    'Charles Moto': './fonts/CharlesMoto.ttf',
    'Charles Moto Radial': './fonts/CharlesMotoRadial.ttf',
    'Mandatory': './fonts/Meena-Mandatory.ttf',
    'Metro Radial': './fonts/MetroRadial.ttf',
    'DIN Next': './fonts/DINNext.otf',
    'Orbitron': './fonts/Orbitron.ttf',
    'Retro': './fonts/Retro.ttf',
    'RetroSkinny': './fonts/RetroSkinny.ttf'
  };

  const fontPromises = Object.entries(fontMap).map(async ([fontFamily, fontPath]) => {
    const base64 = await fontToBase64(fontPath);
    if (base64) {
      // Extract the base64 data (remove data:font/truetype;base64, prefix if present)
      const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
      const format = fontPath.endsWith('.otf') ? 'opentype' : 'truetype';

      return `@font-face {
        font-family: '${fontFamily}';
        src: url('data:font/${format === 'opentype' ? 'opentype' : 'truetype'};charset=utf-8;base64,${base64Data}') format('${format}');
        font-weight: normal;
        font-style: normal;
      }`;
    }
    return null;
  });

  const builtInFontRules = (await Promise.all(fontPromises)).filter(rule => rule !== null).join('\n');

  // Add custom uploaded fonts
  const customFontRules: string[] = [];
  for (const [fontName, base64Data] of customFontRegistry.entries()) {
    // Extract the base64 data if it has a data URI prefix
    const cleanBase64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
    customFontRules.push(`@font-face {
      font-family: '${fontName}';
      src: url('data:font/truetype;charset=utf-8;base64,${cleanBase64}') format('truetype');
      font-weight: normal;
      font-style: normal;
    }`);
  }

  const allFontRules = [builtInFontRules, ...customFontRules].filter(r => r).join('\n');

  // Cache the result
  fontCSSCache = allFontRules;
  fontCacheTimestamp = now;

  console.log(`[PDF Export] Loaded ${Object.keys(fontMap).length} built-in fonts and ${customFontRegistry.size} custom fonts`);

  return allFontRules;
};

// Helper to inject pre-loaded font CSS into a document/element
const injectFontCSS = (targetDoc: Document, targetElement: HTMLElement, fontCSS: string): void => {
  if (fontCSS) {
    const style = targetDoc.createElement('style');
    style.textContent = fontCSS;
    // Insert at the beginning of the target element so fonts are available
    if (targetElement.firstChild) {
      targetElement.insertBefore(style, targetElement.firstChild);
    } else {
      targetElement.appendChild(style);
    }
  }
};

// Image conversion cache to avoid re-processing the same images
const imageCache = new Map<string, string>();

// Helper to convert an image URL/Path to a Base64 DataURI
const imageToDataURL = async (img: HTMLImageElement, scale: number = 1): Promise<string> => {
  return new Promise((resolve) => {
    // If already a data URL, return it immediately (no conversion needed)
    if (img.src.startsWith('data:')) {
      resolve(img.src);
      return;
    }

    // Check cache first
    const cacheKey = `${img.src}_scale_${scale}`;
    if (imageCache.has(cacheKey)) {
      resolve(imageCache.get(cacheKey)!);
      return;
    }

    // If image is already loaded, convert it directly
    if (img.complete && img.naturalWidth > 0 && !img.src.endsWith('.svg') && !img.src.includes('data:image/svg')) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          // Cache the result
          imageCache.set(cacheKey, dataUrl);
          // Clean up canvas immediately
          cleanupCanvas(canvas);
          resolve(dataUrl);
          return;
        }
      } catch (e) {
        console.warn('Failed to convert loaded image:', e);
      }
    }

    // Otherwise, load it fresh
    const tempImg = new Image();
    tempImg.crossOrigin = 'anonymous';
    
    // For SVG images, use the displayed size instead of natural size
    const isSvg = img.src.endsWith('.svg') || img.src.includes('.svg') || img.src.includes('data:image/svg');
    let targetWidth = img.clientWidth || img.width || 200;
    let targetHeight = img.clientHeight || img.height || 200;
    
    // If SVG and we have container dimensions, use those
    if (isSvg) {
      const container = img.parentElement;
      if (container) {
        // Get the actual displayed size (in pixels, which equals mm in our 1px=1mm display)
        const containerRect = container.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(container);
        let containerWidth = containerRect.width || parseFloat(computedStyle.width) || targetWidth;
        let containerHeight = containerRect.height || parseFloat(computedStyle.height) || targetHeight;
        
        targetWidth = containerWidth;
        targetHeight = containerHeight;
      }
      
      // Ensure minimum size for SVG
      if (targetWidth === 0 || targetHeight === 0) {
        targetWidth = 200;
        targetHeight = 200;
      }
    }
    
    tempImg.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        // For SVG, use the target size scaled by the pixel ratio (for high DPI)
        // For other images, use natural size
        if (isSvg) {
          canvas.width = targetWidth * scale;
          canvas.height = targetHeight * scale;
        } else {
          canvas.width = tempImg.naturalWidth || targetWidth;
          canvas.height = tempImg.naturalHeight || targetHeight;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/png');
          // Cache the result
          imageCache.set(cacheKey, dataUrl);
          // Clean up canvas immediately
          cleanupCanvas(canvas);
          resolve(dataUrl);
        } else {
          resolve(img.src); // Fallback to original
        }
      } catch (e) {
        console.warn('Failed to convert image to data URL:', e);
        resolve(img.src); // Fallback to original
      }
    };
    
    tempImg.onerror = () => {
      console.warn('Failed to load image for conversion:', img.src);
      resolve(img.src); // Fallback to original
    };
    
    tempImg.src = resolveImagePath(img);
  });
};

interface PdfExportOptions {
  offsetX?: number;
  offsetY?: number;
  lengthCalibration?: number;
  rotate?: boolean;
  mirror?: boolean;
  enableCropMarks?: boolean;
  cropMarksOffset?: number; // Bleed in mm - adds whitespace in Flexible mode, controls crop mark position in Fixed mode
  enableSideGuidelines?: boolean;
  sideGuidelinesPosition?: 'side' | 'top' | 'both';
  sideGuidelinesOffset?: number; // Offset outwards from artwork edge (0 = at edge, positive = into whitespace)
  pageWidth?: number; // Physical paper width (mm)
  pageHeight?: number; // Physical paper height (mm)
  topAligned?: boolean; // If true, position artwork at top instead of centering (useful for plates with lips)
  mediaSize?: 'FLEXIBLE' | 'FIXED'; // FLEXIBLE = PDF matches artwork + bleed, FIXED = PDF is exactly pageWidth x pageHeight
}

export const exportPlateToPDF = async (
  elementId: string, 
  filename: string, 
  width: number, 
  height: number, 
  dpi: number = 300,
  options: PdfExportOptions = {}
) => {
  const {
    offsetX = 0,
    offsetY = 0,
    lengthCalibration = 100,
    rotate = false,
    mirror = false,
    enableCropMarks = false,
    cropMarksOffset = 5,
    enableSideGuidelines = false,
    sideGuidelinesPosition = 'side',
    sideGuidelinesOffset = 0, // Default 0 = at artwork edge
    pageWidth: providedPageWidth,
    pageHeight: providedPageHeight,
    topAligned = false,
    mediaSize = 'FLEXIBLE'
  } = options;
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  // Store original styles to restore later
  const originalPosition = element.style.position;
  const originalLeft = element.style.left;
  const originalTop = element.style.top;
  const originalWidth = element.style.width;
  const originalHeight = element.style.height;
  const originalZIndex = element.style.zIndex;
  
  // Temporarily make element visible and properly sized for capture
  element.style.position = 'fixed';
  element.style.left = '0';
  element.style.top = '0';
  element.style.width = `${width}px`;
  element.style.height = `${height}px`;
  element.style.zIndex = '9999';
  element.style.visibility = 'visible';
  element.style.opacity = '1';

  // Force wait for fonts to load - check all custom fonts
  await document.fonts.ready;
  
  // Wait for all fonts to be loaded, especially custom fonts
  const fontFamilies = [
    'Charles Wright Radial', 'Charles Wright', 'Charles Moto', 'Charles Moto Radial',
    'Euro', 'Mandatory', 'Metro Radial', 'Orbitron', 'DIN Next', 'Arial'
  ];
  
  // Load fonts with multiple sizes to ensure they're ready
  const fontPromises: Promise<any>[] = [];
  for (const fontFamily of fontFamilies) {
    for (const size of [12, 16, 24, 48, 72, 99]) {
      try {
        fontPromises.push(document.fonts.load(`${size}px "${fontFamily}"`));
      } catch (e) {
        // Font might not be available, continue
      }
    }
  }
  
  // Wait for all fonts to load
  await Promise.allSettled(fontPromises);
  
  // Additional wait to ensure fonts are rendered in the DOM
  await new Promise(resolve => setTimeout(resolve, 500));
  let _ = element.offsetHeight; // Force reflow

  // --- STEP 1: CLONE AND PRE-PROCESS IMAGES (BEFORE html-to-image) ---
  // Clone the element to work on it safely
  const clonedNode = element.cloneNode(true) as HTMLElement;
  // Ensure the cloned node has the same ID
  clonedNode.id = elementId;
  
  // Ensure cloned node has the correct size (1px = 1mm)
  clonedNode.style.width = `${width}px`;
  clonedNode.style.height = `${height}px`;
  clonedNode.style.position = 'relative';
  clonedNode.style.display = 'block';
  
  // Create a hidden container to hold our clone while we work on it
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.visibility = 'hidden';
  container.appendChild(clonedNode);
  document.body.appendChild(container);

  // Find all images in our CLONE and convert them to Data64 *before* screenshotting
  const clonedImages = clonedNode.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
  const imagePromises = Array.from(clonedImages).map(async (img) => {
    if (img.src && !img.src.startsWith('data:')) {
      // For SVG images, ensure they're loaded at full size
      if (img.src.endsWith('.svg') || img.src.includes('.svg') || img.src.includes('data:image/svg')) {
        // Force SVG to load at container size for proper rasterization
        const container = img.parentElement;
        if (container) {
          // Get the actual displayed size of the container
          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width || container.clientWidth || width;
          const containerHeight = containerRect.height || container.clientHeight || height;
          
          // Use container dimensions for all SVG images
          // But only if the image doesn't have explicit dimensions set!
          const hasExplicitWidth = img.style.width && img.style.width !== 'auto' && img.style.width !== '100%';
          const hasExplicitHeight = img.style.height && img.style.height !== 'auto' && img.style.height !== '100%';
          
          if (!hasExplicitWidth || !hasExplicitHeight) {
            img.style.width = `${containerWidth}px`;
            img.style.height = `${containerHeight}px`;
            img.style.maxWidth = 'none';
            img.style.maxHeight = 'none';
          }
        }
      }
      
      const dataUrl = await imageToDataURL(img, pixelRatio);
      img.src = dataUrl;
      // Wait for the image to load with the new data URL
      return new Promise<void>((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Continue even if fails
        }
      });
    }
  });

  // Wait for all images to become Base64 and load
  await Promise.all(imagePromises);
  
  // --- STEP 1.5: PRE-LOAD FONTS AS BASE64 ---
  // Pre-load all fonts as base64 CSS before html2canvas runs
  const fontCSS = await preloadFontsAsBase64();
  
  // Reduced delay - minimal time needed for layout
  await new Promise(resolve => setTimeout(resolve, 50));
  let __ = clonedNode.offsetHeight; // Force reflow

  // --- STEP 2: CAPTURE WITH html-to-image ---
  // html-to-image is more reliable and handles modern CSS better than html2canvas
  // It properly captures HTML text elements that html2canvas was missing
  
  // Ensure HTML text elements are visible before capture
  const htmlTextContainers = clonedNode.querySelectorAll('[data-main-text-html="true"]');
  htmlTextContainers.forEach((container) => {
    const htmlContainer = container as HTMLElement;
    htmlContainer.style.visibility = 'visible';
    htmlContainer.style.opacity = '1';
    htmlContainer.style.zIndex = '999';
    htmlContainer.style.pointerEvents = 'none';
    // Force all child text elements to be visible
    const textDivs = htmlContainer.querySelectorAll('div');
    textDivs.forEach((div) => {
      div.style.visibility = 'visible';
      div.style.opacity = '1';
      div.style.display = 'block';
    });
  });
  
  // Calculate pixelRatio based on DPI
  // 1 inch = 25.4mm, so 1mm = (dpi / 25.4) pixels
  // Element is sized at 1px = 1mm for display
  // For export at specified DPI: pixelRatio = (dpi / 25.4)
  // This ensures the output matches the DPI setting
  const pixelsPerMM = dpi / 25.4;
  const pixelRatio = pixelsPerMM; // This gives us the correct DPI output
  
  let canvas;
  try {
    // Capture with explicit dimensions
    // Element is sized at 1px = 1mm for display, but we need to capture at DPI resolution
    canvas = await toCanvas(clonedNode, {
      pixelRatio: pixelRatio, // Set based on DPI
      backgroundColor: '#ffffff',
      width: width, // Width in px (equals mm in display)
      height: height, // Height in px (equals mm in display)
      cacheBust: true,
      fontEmbedCSS: fontCSS || undefined,
      quality: 1.0, // Maximum quality
      imagePlaceholder: undefined, // Don't use placeholders, wait for images
    });

    // --- STEP 3: PDF EXPORT ---
    if (!canvas || typeof canvas.toDataURL !== 'function') {
      throw new Error(`Failed to capture canvas - html-to-image returned: ${canvas}`);
    }

    let imgData: string;
    try {
      imgData = canvas.toDataURL('image/png');
    } catch (e) {
      throw new Error(`Failed to convert canvas to data URL: ${e instanceof Error ? e.message : String(e)}`);
    }
    
    if (!imgData || imgData.length === 0) {
      throw new Error('Canvas toDataURL returned empty string');
    }

    // Determine final page and artwork dimensions based on mediaSize mode
    let finalPageWidth: number;
    let finalPageHeight: number;
    let artworkWidth: number;
    let artworkHeight: number;
    const isFixedMedia = mediaSize === 'FIXED' && providedPageWidth !== undefined && providedPageHeight !== undefined;

    if (isFixedMedia) {
      // FIXED mode: PDF is exactly pageWidth x pageHeight
      // Artwork is NOT scaled - it's placed at original size, centered on the canvas
      finalPageWidth = providedPageWidth!;
      finalPageHeight = providedPageHeight!;

      // Artwork dimensions are the original size (with rotation swap if needed)
      if (rotate) {
        artworkWidth = height;
        artworkHeight = width;
      } else {
        artworkWidth = width;
        artworkHeight = height;
      }
    } else {
      // FLEXIBLE mode: PDF size = artwork size exactly (no whitespace unless crop marks)
      // Ignore saved page dimensions - use artwork dimensions directly
      const calibrationScale = lengthCalibration / 100;

      if (rotate) {
        artworkWidth = height;
        artworkHeight = width * calibrationScale;
      } else {
        artworkWidth = width * calibrationScale;
        artworkHeight = height;
      }

      // Page size = artwork + crop offset (if enabled)
      const cropOffset = enableCropMarks ? cropMarksOffset : 0;
      finalPageWidth = artworkWidth + (cropOffset * 2);
      finalPageHeight = artworkHeight + (cropOffset * 2);
    }

    // Calculate available gaps for centering, crop marks, and guidelines
    const availableGapX = finalPageWidth - artworkWidth;
    const availableGapY = finalPageHeight - artworkHeight;

    const pdf = new jsPDF({
      orientation: finalPageWidth > finalPageHeight ? 'l' : 'p',
      unit: 'mm',
      format: [finalPageWidth, finalPageHeight],
      compress: true
    });

    // Calculate artwork position - ALWAYS center artwork on canvas
    let artworkX: number;
    let artworkY: number;

    // Center horizontally
    artworkX = availableGapX / 2 + offsetX;

    // Center or top-align vertically
    if (topAligned) {
      // Position at top (useful for plates with lips that extend from bottom)
      artworkY = offsetY;
    } else {
      // Center artwork on page
      artworkY = availableGapY / 2 + offsetY;
    }

    // Apply transformations by manipulating canvas before adding to PDF
    const transformedCanvas = document.createElement('canvas');
    const ctx = transformedCanvas.getContext('2d');
    if (!ctx) {
      cleanupCanvas(canvas);
      throw new Error('Failed to create canvas context');
    }

    let transformedImgData: string;
    try {
    // Calculate transformed canvas size
    if (rotate) {
      transformedCanvas.width = canvas.height;
      transformedCanvas.height = canvas.width;
    } else {
      transformedCanvas.width = canvas.width;
      transformedCanvas.height = canvas.height;
    }

    // Apply transformations
    ctx.save();
    
    if (rotate) {
      ctx.translate(transformedCanvas.width / 2, transformedCanvas.height / 2);
      ctx.rotate(Math.PI / 2);
      if (mirror) ctx.scale(-1, 1);
      ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
    } else {
      if (mirror) {
        ctx.translate(transformedCanvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(canvas, 0, 0);
    }
    
    ctx.restore();
    
      transformedImgData = transformedCanvas.toDataURL('image/png');
      
      // Clean up original canvas after transformation
      cleanupCanvas(canvas);
    } finally {
      // Clean up transformed canvas
      cleanupCanvas(transformedCanvas);
    }

    // Add transformed image
    pdf.addImage(transformedImgData, 'PNG', artworkX, artworkY, artworkWidth, artworkHeight, undefined, 'FAST');

    // Draw crop marks if enabled - only when there's enough space in the whitespace
    // Crop marks draw at artwork corners, into the whitespace (not expanding the PDF)
    const minCropSpace = cropMarksOffset * 2;
    const canDrawCropMarks = enableCropMarks && availableGapX >= minCropSpace && availableGapY >= minCropSpace;

    if (canDrawCropMarks) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);

      const markLength = 3; // Length of crop mark lines in mm
      const gap = 0.5; // Small gap between mark and artwork edge

      // Top-left corner (marks extend into whitespace from artwork corner)
      if (artworkX >= markLength) {
        pdf.line(artworkX - markLength, artworkY, artworkX - gap, artworkY); // horizontal
      }
      if (artworkY >= markLength) {
        pdf.line(artworkX, artworkY - markLength, artworkX, artworkY - gap); // vertical
      }

      // Top-right corner
      if (artworkX + artworkWidth + markLength <= finalPageWidth) {
        pdf.line(artworkX + artworkWidth + gap, artworkY, artworkX + artworkWidth + markLength, artworkY);
      }
      if (artworkY >= markLength) {
        pdf.line(artworkX + artworkWidth, artworkY - markLength, artworkX + artworkWidth, artworkY - gap);
      }

      // Bottom-left corner
      if (artworkX >= markLength) {
        pdf.line(artworkX - markLength, artworkY + artworkHeight, artworkX - gap, artworkY + artworkHeight);
      }
      if (artworkY + artworkHeight + markLength <= finalPageHeight) {
        pdf.line(artworkX, artworkY + artworkHeight + gap, artworkX, artworkY + artworkHeight + markLength);
      }

      // Bottom-right corner
      if (artworkX + artworkWidth + markLength <= finalPageWidth) {
        pdf.line(artworkX + artworkWidth + gap, artworkY + artworkHeight, artworkX + artworkWidth + markLength, artworkY + artworkHeight);
      }
      if (artworkY + artworkHeight + markLength <= finalPageHeight) {
        pdf.line(artworkX + artworkWidth, artworkY + artworkHeight + gap, artworkX + artworkWidth, artworkY + artworkHeight + markLength);
      }
    }

    // Draw guidelines if enabled - lines draw OUTSIDE artwork, moving into whitespace
    // Used for aligning acrylic blocks when printing short plates on large media
    const minGuidelineGap = 2; // Minimum gap required to draw guidelines (mm)

    if (enableSideGuidelines) {
      pdf.setDrawColor(128, 128, 128); // Gray for guide lines
      pdf.setLineWidth(0.15);

      // Set dashed line pattern [dash length, gap length]
      if (typeof pdf.setLineDashPattern === 'function') {
        pdf.setLineDashPattern([2, 2], 0);
      }

      // Side guidelines (vertical lines) - only if horizontal gap is sufficient
      if ((sideGuidelinesPosition === 'side' || sideGuidelinesPosition === 'both') && availableGapX > minGuidelineGap) {
        // Left guideline: at artworkX - offset (moves LEFT into whitespace)
        const leftGuideX = artworkX - sideGuidelinesOffset;
        // Right guideline: at (artworkX + artworkWidth) + offset (moves RIGHT into whitespace)
        const rightGuideX = artworkX + artworkWidth + sideGuidelinesOffset;

        // Draw if within page bounds
        if (leftGuideX >= 0) {
          pdf.line(leftGuideX, 0, leftGuideX, finalPageHeight);
        }
        if (rightGuideX <= finalPageWidth) {
          pdf.line(rightGuideX, 0, rightGuideX, finalPageHeight);
        }
      }

      // Top/bottom guidelines (horizontal lines) - only if vertical gap is sufficient
      if ((sideGuidelinesPosition === 'top' || sideGuidelinesPosition === 'both') && availableGapY > minGuidelineGap) {
        // Top guideline: at artworkY - offset (moves UP into whitespace)
        const topGuideY = artworkY - sideGuidelinesOffset;
        // Bottom guideline: at (artworkY + artworkHeight) + offset (moves DOWN into whitespace)
        const bottomGuideY = artworkY + artworkHeight + sideGuidelinesOffset;

        // Draw if within page bounds
        if (topGuideY >= 0) {
          pdf.line(0, topGuideY, finalPageWidth, topGuideY);
        }
        if (bottomGuideY <= finalPageHeight) {
          pdf.line(0, bottomGuideY, finalPageWidth, bottomGuideY);
        }
      }

      // Reset dash pattern to solid
      if (typeof pdf.setLineDashPattern === 'function') {
        pdf.setLineDashPattern([], 0);
      }
    }

    return await savePDF(pdf, `${filename}.pdf`);

  } catch (err) {
    console.error("Export failed", err);
    // Log more details about the error
    if (err instanceof Error) {
      console.error("Error message:", err.message);
      console.error("Error stack:", err.stack);
    }
    throw err;
  } finally {
    // Clean up the hidden DOM element
    cleanupClonedNode(container);
    
    // Restore original styles
    element.style.position = originalPosition;
    element.style.left = originalLeft;
    element.style.top = originalTop;
    element.style.width = originalWidth;
    element.style.height = originalHeight;
    element.style.zIndex = originalZIndex;
  }
};

export interface BulkExportProgress {
  current: number;
  total: number;
  cancelled: boolean;
}

export const exportBulkPlatesToPDF = async (
  renderCallback: (index: number) => Promise<void>,
  count: number,
  dims: Dimensions,
  elementId: string,
  filenamePrefix: string = 'Bulk_Plates',
  dpi: number = 300,
  options: PdfExportOptions = {},
  onProgress?: (progress: BulkExportProgress) => void,
  shouldCancel?: () => boolean
) => {
  const {
    offsetX = 0,
    offsetY = 0,
    lengthCalibration = 100,
    rotate = false,
    mirror = false,
    enableCropMarks = false,
    cropMarksOffset = 5
  } = options;

  const calibrationScale = lengthCalibration / 100;
  const cropOffset = enableCropMarks ? cropMarksOffset : 0;
  
  // Calculate page dimensions with all transformations
  const rawWidth = (rotate ? dims.height : dims.width) + (cropOffset * 2);
  const rawHeight = (rotate ? dims.width : dims.height) + (cropOffset * 2);
  const pageWidth = rotate ? rawWidth : rawWidth * calibrationScale;
  const pageHeight = rotate ? rawHeight * calibrationScale : rawHeight;

  const pdf = new jsPDF({
    orientation: pageWidth > pageHeight ? 'l' : 'p',
    unit: 'mm',
    format: [pageWidth, pageHeight],
    compress: true
  });

  // Pre-load fonts once
  await document.fonts.ready;
  const fontFamilies = [
    'Charles Wright Radial', 'Charles Wright', 'Charles Moto', 'Charles Moto Radial',
    'Euro', 'Mandatory', 'Metro Radial', 'Orbitron', 'DIN Next'
  ];
  for (const fontFamily of fontFamilies) {
    try {
      await document.fonts.load(`12px "${fontFamily}"`);
    } catch (e) {}
  }
  // Reduced delay - minimal time needed
  await new Promise(resolve => setTimeout(resolve, 50));

  const pixelRatio = dpi / 25.4; // Convert DPI to pixels per mm
  let pagesAdded = 0;

  for (let i = 0; i < count; i++) {
    // Check for cancellation
    if (shouldCancel && shouldCancel()) {
      onProgress?.({ current: i, total: count, cancelled: true });
      return; // Exit without saving
    }

    // Report progress
    onProgress?.({ current: i, total: count, cancelled: false });

    // Call the render callback and wait for React to update
    await renderCallback(i);
    
    // Reduced delay - React updates are usually faster
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Element ${elementId} not found for page ${i}`);
      continue;
    }

    // Force a layout recalculation
    let ___ = element.offsetHeight;
    
    try {
      const canvas = await toCanvas(element, {
        pixelRatio: pixelRatio,
        backgroundColor: '#ffffff',
        cacheBust: true,
        quality: 1.0,
      });

      // Apply transformations (rotation, mirroring)
      let finalCanvas = canvas;
      if (rotate || mirror) {
        const transformedCanvas = document.createElement('canvas');
        const ctx = transformedCanvas.getContext('2d')!;
        
        if (rotate) {
          transformedCanvas.width = canvas.height;
          transformedCanvas.height = canvas.width;
          ctx.translate(transformedCanvas.width / 2, transformedCanvas.height / 2);
          ctx.rotate(Math.PI / 2);
          if (mirror) ctx.scale(-1, 1);
          ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
        } else if (mirror) {
          transformedCanvas.width = canvas.width;
          transformedCanvas.height = canvas.height;
          ctx.translate(transformedCanvas.width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(canvas, 0, 0);
        }
        finalCanvas = transformedCanvas;
      }

      const imgData = finalCanvas.toDataURL('image/png');
      
      // Clean up canvases immediately after use
      cleanupCanvas(canvas);
      if (finalCanvas !== canvas) {
        cleanupCanvas(finalCanvas);
      }
      
      if (pagesAdded > 0) {
        // Ensure consistent page size and orientation for all pages
        pdf.addPage([pageWidth, pageHeight], pageWidth > pageHeight ? 'l' : 'p');
      }
      
      // Calculate artwork dimensions - MUST match the transformed canvas dimensions
      // When rotated, canvas dimensions are swapped, so use the transformed canvas size
      // pixelRatio = dpi / 25.4, so to convert pixels to mm: pixels / pixelRatio
      const artworkWidth = finalCanvas.width / pixelRatio; // Convert pixels to mm
      const artworkHeight = finalCanvas.height / pixelRatio;
      const artworkX = cropOffset + offsetX;
      const artworkY = cropOffset + offsetY;
      
      pdf.addImage(imgData, 'PNG', artworkX, artworkY, artworkWidth, artworkHeight);
      pagesAdded++;

      // Draw crop marks if enabled
      if (enableCropMarks) {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.2);
        const markLength = 5;
        // Top-left
        pdf.line(cropOffset, 0, cropOffset, markLength);
        pdf.line(0, cropOffset, markLength, cropOffset);
        // Top-right
        pdf.line(pageWidth - cropOffset, 0, pageWidth - cropOffset, markLength);
        pdf.line(pageWidth - markLength, cropOffset, pageWidth, cropOffset);
        // Bottom-left
        pdf.line(cropOffset, pageHeight - markLength, cropOffset, pageHeight);
        pdf.line(0, pageHeight - cropOffset, markLength, pageHeight - cropOffset);
        // Bottom-right
        pdf.line(pageWidth - cropOffset, pageHeight - markLength, pageWidth - cropOffset, pageHeight);
        pdf.line(pageWidth - markLength, pageHeight - cropOffset, pageWidth, pageHeight - cropOffset);
      }
    } catch (err) {
      console.error(`Failed to capture page ${i}:`, err);
    }
  }

  // Final progress update
  onProgress?.({ current: count, total: count, cancelled: false });

  if (pagesAdded > 0) {
    await savePDF(pdf, `${filenamePrefix}_${Date.now()}.pdf`);
  }
};

/**
 * Generate PDF blob using the same logic as exportPlateToPDF for consistency.
 * This ensures fonts, images, and transformations are handled correctly.
 */
export const generatePlatePDFBlob = async (
  elementId: string, 
  width: number, 
  height: number,
  rotate: boolean = false,
  dpi: number = 203,
  options: PdfExportOptions = {},
  pageWidth?: number, // Physical paper width (mm) - if provided, artwork will be centered on this page
  pageHeight?: number // Physical paper height (mm) - if provided, artwork will be centered on this page
): Promise<Blob> => {
  console.time('[PDF] Total PDF generation');
  console.log('[PDF] Starting PDF generation at:', new Date().toISOString());
  
  // Use the same export logic but return a blob instead of saving
  // This ensures consistency between export and print
  const {
    offsetX = 0,
    offsetY = 0,
    lengthCalibration = 100,
    mirror = false,
    enableCropMarks = false,
    cropMarksOffset = 5,
    enableSideGuidelines = false,
    sideGuidelinesPosition = 'side',
    sideGuidelinesOffset = 0, // Default 0 = at artwork edge
    topAligned = false,
    mediaSize = 'FLEXIBLE'
  } = options;
  
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  // Store original styles to restore later
  const originalPosition = element.style.position;
  const originalLeft = element.style.left;
  const originalTop = element.style.top;
  const originalWidth = element.style.width;
  const originalHeight = element.style.height;
  const originalZIndex = element.style.zIndex;
  const originalVisibility = element.style.visibility;
  const originalOpacity = element.style.opacity;

  try {
    // Temporarily make element visible and properly positioned for capture
    element.style.position = 'fixed';
    element.style.left = '0';
    element.style.top = '0';
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.zIndex = '9999';
    element.style.visibility = 'visible';
    element.style.opacity = '1';

    console.time('[PDF] Font loading');
    // Wait for fonts and DOM to be ready - CRITICAL for font rendering
  await document.fonts.ready;
    
    // Load all custom fonts with multiple sizes to ensure they're ready
    const fontFamilies = [
      'Charles Wright Radial', 'Charles Wright', 'Charles Moto', 'Charles Moto Radial',
      'Euro', 'Mandatory', 'Metro Radial', 'Orbitron', 'DIN Next', 'Arial'
    ];
    
    const fontPromises: Promise<any>[] = [];
    for (const fontFamily of fontFamilies) {
      for (const size of [12, 16, 24, 48, 72, 99]) {
        try {
          fontPromises.push(document.fonts.load(`${size}px "${fontFamily}"`));
        } catch (e) {
          // Font might not be available, continue
        }
      }
    }
    
    // Wait for all fonts to load
    await Promise.allSettled(fontPromises);
    console.timeEnd('[PDF] Font loading');
    
    console.time('[PDF] Font CSS generation');
    // Pre-load fonts as base64 CSS (like exportPlateToPDF does)
    const fontCSS = await preloadFontsAsBase64();
    console.timeEnd('[PDF] Font CSS generation');
    
    // Clone the element to work on it safely (same as exportPlateToPDF)
    const clonedNode = element.cloneNode(true) as HTMLElement;
    clonedNode.id = elementId;
    clonedNode.style.width = `${width}px`;
    clonedNode.style.height = `${height}px`;
    clonedNode.style.position = 'relative';
    clonedNode.style.display = 'block';
    
    // Create a hidden container to hold our clone while we work on it
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.visibility = 'hidden';
    container.appendChild(clonedNode);
    document.body.appendChild(container);

    console.time('[PDF] Image processing');
    // Find all images in our CLONE and convert them to Data64 *before* screenshotting
    const clonedImages = clonedNode.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
    const imagePromises = Array.from(clonedImages).map(async (img) => {
      if (img.src && !img.src.startsWith('data:')) {
        // For SVG images, ensure they're loaded at full size
        if (img.src.endsWith('.svg') || img.src.includes('.svg') || img.src.includes('data:image/svg')) {
        // Force SVG to load at container size for proper rasterization
        const container = img.parentElement;
        if (container) {
          // Get the actual displayed size of the container
          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width || container.clientWidth || width;
          const containerHeight = containerRect.height || container.clientHeight || height;
          
          // Use container dimensions for all SVG images
          // But only if the image doesn't have explicit dimensions set!
          const hasExplicitWidth = img.style.width && img.style.width !== 'auto' && img.style.width !== '100%';
          const hasExplicitHeight = img.style.height && img.style.height !== 'auto' && img.style.height !== '100%';
          
          if (!hasExplicitWidth || !hasExplicitHeight) {
            img.style.width = `${containerWidth}px`;
            img.style.height = `${containerHeight}px`;
            img.style.maxWidth = 'none';
            img.style.maxHeight = 'none';
          }
        }
      }
        
        const dataUrl = await imageToDataURL(img, pixelRatio);
        img.src = dataUrl;
        // Wait for the image to load with the new data URL
        return new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even if fails
          }
        });
      }
    });

    // Wait for all images to become Base64 and load
    await Promise.all(imagePromises);
    console.timeEnd('[PDF] Image processing');
    
    console.time('[PDF] Layout wait');
    // Reduced wait - only need minimal time for layout
    await new Promise(resolve => setTimeout(resolve, 50));
    let _ = clonedNode.offsetHeight; // Force reflow
    console.timeEnd('[PDF] Layout wait');

    // Calculate pixelRatio based on DPI (same as exportPlateToPDF)
    const pixelsPerMM = dpi / 25.4;
    const pixelRatio = pixelsPerMM;

    console.time('[PDF] Canvas capture (html-to-image)');
    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = await toCanvas(clonedNode, {
    pixelRatio: pixelRatio,
      backgroundColor: '#ffffff',
      width: width,
      height: height,
    cacheBust: true,
      fontEmbedCSS: fontCSS || undefined,
      quality: 1.0,
    });
    console.timeEnd('[PDF] Canvas capture (html-to-image)');
    } finally {
      // Always clean up the hidden container, even on error
      cleanupClonedNode(container);
    }

    console.log('PDF Blob generation:', {
      elementId,
      width,
      height,
      rotate,
      dpi,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    // Check if canvas has content
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas is empty - element may not have rendered correctly');
    }

    console.time('[PDF] Canvas to data URL');
    let imgData: string;
    try {
      imgData = canvas.toDataURL('image/png');
    } catch (e) {
      throw new Error(`Failed to convert canvas to data URL: ${e instanceof Error ? e.message : String(e)}`);
    }
    console.timeEnd('[PDF] Canvas to data URL');
    
    if (!imgData || imgData.length === 0) {
      throw new Error('Canvas toDataURL returned empty string');
    }

    console.time('[PDF] PDF creation and transformation');

    // Determine final page and artwork dimensions based on mediaSize mode
    let finalPageWidth: number;
    let finalPageHeight: number;
    let artworkWidth: number;
    let artworkHeight: number;
    const isFixedMedia = mediaSize === 'FIXED' && pageWidth !== undefined && pageHeight !== undefined;

    if (isFixedMedia) {
      // FIXED mode: PDF is exactly pageWidth x pageHeight
      // Artwork fills the page - apply calibration so artwork stretches to match page
      finalPageWidth = pageWidth!;
      finalPageHeight = pageHeight!;
      const calibrationScale = lengthCalibration / 100;

      if (rotate) {
        artworkWidth = height;
        artworkHeight = width * calibrationScale;
      } else {
        artworkWidth = width * calibrationScale;
        artworkHeight = height;
      }
    } else {
      // FLEXIBLE mode: PDF size = artwork size exactly (no whitespace unless crop marks)
      // Ignore saved page dimensions - use artwork dimensions directly
      const calibrationScale = lengthCalibration / 100;

      if (rotate) {
        artworkWidth = height;
        artworkHeight = width * calibrationScale;
      } else {
        artworkWidth = width * calibrationScale;
        artworkHeight = height;
      }

      // Page size = artwork + crop offset (if enabled)
      const cropOffset = enableCropMarks ? cropMarksOffset : 0;
      finalPageWidth = artworkWidth + (cropOffset * 2);
      finalPageHeight = artworkHeight + (cropOffset * 2);
    }

    // Calculate available gaps for centering, crop marks, and guidelines
    const availableGapX = finalPageWidth - artworkWidth;
    const availableGapY = finalPageHeight - artworkHeight;

    const pdf = new jsPDF({
      orientation: finalPageWidth > finalPageHeight ? 'l' : 'p',
      unit: 'mm',
      format: [finalPageWidth, finalPageHeight],
      compress: true
    });

    // Calculate artwork position - ALWAYS center artwork on canvas
    let artworkX: number;
    let artworkY: number;

    // Center horizontally
    artworkX = availableGapX / 2 + offsetX;

    // Center or top-align vertically
    if (topAligned) {
      // Position at top (useful for plates with lips that extend from bottom)
      artworkY = offsetY;
    } else {
      // Center artwork on page
      artworkY = availableGapY / 2 + offsetY;
    }

    // Apply transformations by manipulating canvas before adding to PDF (SAME AS exportPlateToPDF)
    const transformedCanvas = document.createElement('canvas');
    const ctx = transformedCanvas.getContext('2d');
    if (!ctx) {
      cleanupCanvas(canvas);
      throw new Error('Failed to create canvas context');
    }

    let transformedImgData: string;
    try {
    // Calculate transformed canvas size
    if (rotate) {
      transformedCanvas.width = canvas.height;
      transformedCanvas.height = canvas.width;
    } else {
      transformedCanvas.width = canvas.width;
      transformedCanvas.height = canvas.height;
    }

    // Apply transformations (SAME AS exportPlateToPDF)
    ctx.save();
    
    if (rotate) {
      ctx.translate(transformedCanvas.width / 2, transformedCanvas.height / 2);
      ctx.rotate(Math.PI / 2);
      if (mirror) ctx.scale(-1, 1);
      ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
    } else {
      if (mirror) {
        ctx.translate(transformedCanvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(canvas, 0, 0);
    }
    
    ctx.restore();
    
      transformedImgData = transformedCanvas.toDataURL('image/png');
    } finally {
      // Clean up original canvas immediately after transformation
      cleanupCanvas(canvas);
    }

    // Add transformed image (SAME AS exportPlateToPDF)
    pdf.addImage(transformedImgData, 'PNG', artworkX, artworkY, artworkWidth, artworkHeight, undefined, 'FAST');

    // Draw crop marks if enabled - only when there's enough space in the whitespace
    // Crop marks draw at artwork corners, into the whitespace (not expanding the PDF)
    const minCropSpace = cropMarksOffset * 2;
    const canDrawCropMarks = enableCropMarks && availableGapX >= minCropSpace && availableGapY >= minCropSpace;

    if (canDrawCropMarks) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);

      const markLength = 3; // Length of crop mark lines in mm
      const gap = 0.5; // Small gap between mark and artwork edge

      // Top-left corner (marks extend into whitespace from artwork corner)
      if (artworkX >= markLength) {
        pdf.line(artworkX - markLength, artworkY, artworkX - gap, artworkY); // horizontal
      }
      if (artworkY >= markLength) {
        pdf.line(artworkX, artworkY - markLength, artworkX, artworkY - gap); // vertical
      }

      // Top-right corner
      if (artworkX + artworkWidth + markLength <= finalPageWidth) {
        pdf.line(artworkX + artworkWidth + gap, artworkY, artworkX + artworkWidth + markLength, artworkY);
      }
      if (artworkY >= markLength) {
        pdf.line(artworkX + artworkWidth, artworkY - markLength, artworkX + artworkWidth, artworkY - gap);
      }

      // Bottom-left corner
      if (artworkX >= markLength) {
        pdf.line(artworkX - markLength, artworkY + artworkHeight, artworkX - gap, artworkY + artworkHeight);
      }
      if (artworkY + artworkHeight + markLength <= finalPageHeight) {
        pdf.line(artworkX, artworkY + artworkHeight + gap, artworkX, artworkY + artworkHeight + markLength);
      }

      // Bottom-right corner
      if (artworkX + artworkWidth + markLength <= finalPageWidth) {
        pdf.line(artworkX + artworkWidth + gap, artworkY + artworkHeight, artworkX + artworkWidth + markLength, artworkY + artworkHeight);
      }
      if (artworkY + artworkHeight + markLength <= finalPageHeight) {
        pdf.line(artworkX + artworkWidth, artworkY + artworkHeight + gap, artworkX + artworkWidth, artworkY + artworkHeight + markLength);
      }
    }

    // Draw guidelines if enabled - lines draw OUTSIDE artwork, moving into whitespace
    // Used for aligning acrylic blocks when printing short plates on large media
    const minGuidelineGap = 2; // Minimum gap required to draw guidelines (mm)

    if (enableSideGuidelines) {
      pdf.setDrawColor(128, 128, 128); // Gray for guide lines
      pdf.setLineWidth(0.15);

      // Set dashed line pattern [dash length, gap length]
      if (typeof pdf.setLineDashPattern === 'function') {
        pdf.setLineDashPattern([2, 2], 0);
      }

      // Side guidelines (vertical lines) - only if horizontal gap is sufficient
      if ((sideGuidelinesPosition === 'side' || sideGuidelinesPosition === 'both') && availableGapX > minGuidelineGap) {
        // Left guideline: at artworkX - offset (moves LEFT into whitespace)
        const leftGuideX = artworkX - sideGuidelinesOffset;
        // Right guideline: at (artworkX + artworkWidth) + offset (moves RIGHT into whitespace)
        const rightGuideX = artworkX + artworkWidth + sideGuidelinesOffset;

        // Draw if within page bounds
        if (leftGuideX >= 0) {
          pdf.line(leftGuideX, 0, leftGuideX, finalPageHeight);
        }
        if (rightGuideX <= finalPageWidth) {
          pdf.line(rightGuideX, 0, rightGuideX, finalPageHeight);
        }
      }

      // Top/bottom guidelines (horizontal lines) - only if vertical gap is sufficient
      if ((sideGuidelinesPosition === 'top' || sideGuidelinesPosition === 'both') && availableGapY > minGuidelineGap) {
        // Top guideline: at artworkY - offset (moves UP into whitespace)
        const topGuideY = artworkY - sideGuidelinesOffset;
        // Bottom guideline: at (artworkY + artworkHeight) + offset (moves DOWN into whitespace)
        const bottomGuideY = artworkY + artworkHeight + sideGuidelinesOffset;

        // Draw if within page bounds
        if (topGuideY >= 0) {
          pdf.line(0, topGuideY, finalPageWidth, topGuideY);
        }
        if (bottomGuideY <= finalPageHeight) {
          pdf.line(0, bottomGuideY, finalPageWidth, bottomGuideY);
        }
      }

      // Reset dash pattern to solid
      if (typeof pdf.setLineDashPattern === 'function') {
        pdf.setLineDashPattern([], 0);
      }
    }
    
    console.time('[PDF] PDF output to blob');
    const blob = pdf.output('blob');
    console.timeEnd('[PDF] PDF output to blob');
    console.timeEnd('[PDF] PDF creation and transformation');
    
    // Clean up transformed canvas after PDF is created
    cleanupCanvas(transformedCanvas);
    
    console.timeEnd('[PDF] Total PDF generation');
    
    console.log('PDF blob created:', {
      size: blob.size,
      width: pageWidth,
      height: pageHeight,
      rotated: rotate,
      dpi: dpi
    });
    
    if (blob.size === 0) {
      throw new Error('Generated PDF blob is empty');
    }
    
    return blob;
  } finally {
    // Restore original styles
    element.style.position = originalPosition;
    element.style.left = originalLeft;
    element.style.top = originalTop;
    element.style.width = originalWidth;
    element.style.height = originalHeight;
    element.style.zIndex = originalZIndex;
    element.style.visibility = originalVisibility;
    element.style.opacity = originalOpacity;
  }
};

// Helper function to capture a plate element and return image data for adding to PDF
export const capturePlateForPDF = async (
  elementId: string,
  width: number,
  height: number,
  dpi: number = 300,
  options: PdfExportOptions = {}
): Promise<{ imgData: string; pageWidth: number; pageHeight: number; artworkWidth: number; artworkHeight: number; artworkX: number; artworkY: number }> => {
  const {
    offsetX = 0,
    offsetY = 0,
    lengthCalibration = 100,
    rotate = false,
    mirror = false,
    enableCropMarks = false,
    cropMarksOffset = 5
  } = options;

  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  // Store original styles
  const originalPosition = element.style.position;
  const originalLeft = element.style.left;
  const originalTop = element.style.top;
  const originalWidth = element.style.width;
  const originalHeight = element.style.height;
  const originalZIndex = element.style.zIndex;
  
  // Temporarily make element visible
  element.style.position = 'fixed';
  element.style.left = '0';
  element.style.top = '0';
  element.style.width = `${width}px`;
  element.style.height = `${height}px`;
  element.style.zIndex = '9999';
  element.style.visibility = 'visible';
  element.style.opacity = '1';

  await document.fonts.ready;
  // Reduced delay - minimal time needed
  await new Promise(resolve => setTimeout(resolve, 50));
  let _______ = element.offsetHeight; // Force reflow

  // Clone element
  const clonedNode = element.cloneNode(true) as HTMLElement;
  clonedNode.id = elementId;
  clonedNode.style.width = `${width}px`;
  clonedNode.style.height = `${height}px`;
  clonedNode.style.position = 'relative';
  clonedNode.style.display = 'block';
  
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.visibility = 'hidden';
  container.appendChild(clonedNode);
  document.body.appendChild(container);

  // Convert images to data URLs
  const clonedImages = clonedNode.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
  const imagePromises = Array.from(clonedImages).map(async (img) => {
    if (img.src && !img.src.startsWith('data:')) {
      const dataUrl = await imageToDataURL(img, pixelRatio);
      img.src = dataUrl;
      return new Promise<void>((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }
      });
    }
  });
  await Promise.all(imagePromises);
  // Reduced delay - minimal time needed for layout
  await new Promise(resolve => setTimeout(resolve, 50));
  let _ = clonedNode.offsetHeight;

  // Make HTML text visible
  const htmlTextContainers = clonedNode.querySelectorAll('[data-main-text-html="true"]');
  htmlTextContainers.forEach((container) => {
    const htmlContainer = container as HTMLElement;
    htmlContainer.style.visibility = 'visible';
    htmlContainer.style.opacity = '1';
    htmlContainer.style.zIndex = '999';
    htmlContainer.style.pointerEvents = 'none';
    const textDivs = htmlContainer.querySelectorAll('div');
    textDivs.forEach((div) => {
      div.style.visibility = 'visible';
      div.style.opacity = '1';
      div.style.display = 'block';
    });
  });

  const pixelsPerMM = dpi / 25.4;
  const pixelRatio = pixelsPerMM;

  const canvas = await toCanvas(clonedNode, {
    pixelRatio: pixelRatio,
    backgroundColor: '#ffffff',
    cacheBust: true,
    quality: 1.0,
  });

  // Clean up cloned node immediately
  cleanupClonedNode(container);
  
  // Restore original styles
  element.style.position = originalPosition;
  element.style.left = originalLeft;
  element.style.top = originalTop;
  element.style.width = originalWidth;
  element.style.height = originalHeight;
  element.style.zIndex = originalZIndex;

  // Apply transformations
  let finalCanvas = canvas;
  let transformedCanvas: HTMLCanvasElement | null = null;
  let imgData: string;
  
  try {
  if (rotate || mirror) {
      transformedCanvas = document.createElement('canvas');
    const ctx = transformedCanvas.getContext('2d')!;
    
    if (rotate) {
      transformedCanvas.width = canvas.height;
      transformedCanvas.height = canvas.width;
      ctx.translate(transformedCanvas.width / 2, transformedCanvas.height / 2);
      ctx.rotate(Math.PI / 2);
      if (mirror) ctx.scale(-1, 1);
      ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
    } else if (mirror) {
      transformedCanvas.width = canvas.width;
      transformedCanvas.height = canvas.height;
      ctx.translate(transformedCanvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(canvas, 0, 0);
    }
    finalCanvas = transformedCanvas;
  }

    imgData = finalCanvas.toDataURL('image/png');
    
    // Clean up original canvas after transformation
    cleanupCanvas(canvas);
  } finally {
    // Clean up transformed canvas if it was created
    if (transformedCanvas && transformedCanvas !== finalCanvas) {
      cleanupCanvas(transformedCanvas);
    }
  }

  // Calculate page dimensions
  const calibrationScale = lengthCalibration / 100;
  const cropOffset = enableCropMarks ? cropMarksOffset : 0;
  const rawWidth = (rotate ? height : width) + (cropOffset * 2);
  const rawHeight = (rotate ? width : height) + (cropOffset * 2);
  const pageWidth = rotate ? rawWidth : rawWidth * calibrationScale;
  const pageHeight = rotate ? rawHeight * calibrationScale : rawHeight;
  const artworkWidth = rotate ? height : width * calibrationScale;
  const artworkHeight = rotate ? width * calibrationScale : height;
  const artworkX = cropOffset + offsetX;
  const artworkY = cropOffset + offsetY;

  return { imgData, pageWidth, pageHeight, artworkWidth, artworkHeight, artworkX, artworkY };
};

// Build a multi-page PDF from multiple plates
export const buildMultiPagePDF = async (
  captures: Array<{ imgData: string; pageWidth: number; pageHeight: number; artworkWidth: number; artworkHeight: number; artworkX: number; artworkY: number; enableCropMarks: boolean; cropMarksOffset: number }>,
  filename: string
) => {
  if (captures.length === 0) return;

  const firstPage = captures[0];
  const pdf = new jsPDF({
    orientation: firstPage.pageWidth > firstPage.pageHeight ? 'l' : 'p',
    unit: 'mm',
    format: [firstPage.pageWidth, firstPage.pageHeight],
    compress: true
  });

  for (let i = 0; i < captures.length; i++) {
    const capture = captures[i];
    
    if (i > 0) {
      pdf.addPage([capture.pageWidth, capture.pageHeight], capture.pageWidth > capture.pageHeight ? 'l' : 'p');
    }

    pdf.addImage(capture.imgData, 'PNG', capture.artworkX, capture.artworkY, capture.artworkWidth, capture.artworkHeight);

    // Draw crop marks if enabled
    if (capture.enableCropMarks) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);
      const markLength = 5;
      const cropOffset = capture.cropMarksOffset;
      // Top-left
      pdf.line(cropOffset, 0, cropOffset, markLength);
      pdf.line(0, cropOffset, markLength, cropOffset);
      // Top-right
      pdf.line(capture.pageWidth - cropOffset, 0, capture.pageWidth - cropOffset, markLength);
      pdf.line(capture.pageWidth - markLength, cropOffset, capture.pageWidth, cropOffset);
      // Bottom-left
      pdf.line(cropOffset, capture.pageHeight - markLength, cropOffset, capture.pageHeight);
      pdf.line(0, capture.pageHeight - cropOffset, markLength, capture.pageHeight - cropOffset);
      // Bottom-right
      pdf.line(capture.pageWidth - cropOffset, capture.pageHeight - markLength, capture.pageWidth - cropOffset, capture.pageHeight);
      pdf.line(capture.pageWidth - markLength, capture.pageHeight - cropOffset, capture.pageWidth, capture.pageHeight - cropOffset);
    }
  }

  await savePDF(pdf, filename);
};
